import { Calendar } from "@fluentui/react-calendar-compat";
import { Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { CalendarMonth20Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { AnalysisResultSection, AnalysisResultStatus } from "../../../../services/AnalysisResultService";

export type SectionStatusPillProps = {
  label: string;
  tone: "neutral" | "progress" | "review" | "done";
};

export const containerClassName = "min-w-0 overflow-hidden rounded-[3px] bg-white dark:bg-oi-dark-background text-primary-dark dark:text-primary-light";
export const tableHeaderClassName = "bg-primary-dark/10 dark:bg-black/40 text-[11px] text-primary-dark dark:text-white";
export const tableCellClassName = "border border-primary-dark/5 dark:border-white/5 px-2 py-0.5 text-[12px] text-oi-button-blue dark:text-oi-yellow";
export const flatSummaryCellClassName = "border border-primary-dark/5 dark:border-white/5 px-2 py-0 text-[12px] text-oi-button-blue dark:text-oi-yellow";
export const flatSummaryLabelCellClassName = "border border-primary-dark/5 dark:border-white/5 px-2 py-0 text-[12px] text-primary-dark/85 dark:text-white/85";
export const inputClassName = "h-6 w-[50px] text-center border border-primary-dark/20 dark:border-white/20 bg-primary-light dark:bg-oi-dark-background px-2 text-[12px] text-oi-button-blue dark:text-oi-yellow outline-none placeholder:text-oi-button-blue/60 dark:placeholder:text-oi-yellow/60";
export const readonlyCellClassName = "flex h-6 w-full min-w-[48px] items-center justify-center px-2 text-[12px] font-semibold text-oi-button-blue dark:text-oi-yellow";
export const labelClassName = "text-[10px] font-semibold text-primary-dark/80 dark:text-white/80";
export const staticValueClassName = "min-w-0 truncate text-[10px] text-primary-dark/65 dark:text-white/65";
export const SAMPLE_COLUMN_WIDTH_CLASS = "w-[112px]";
export const SAMPLE_CELL_CLASSNAME = "whitespace-nowrap";
export const SUMMARY_SAMPLE_COLUMN_WIDTH_CLASS = "w-[104px]";
export const SUMMARY_VALUE_COLUMN_WIDTH_CLASS = "w-[70px]";

export function formatChemistryRowLabel(rowLabel: string) {
  if (rowLabel === "Ctot") {
    return <>C<sub className="text-[9px] align-sub">tot</sub></>;
  }

  if (rowLabel === "Cfree") {
    return <>C<sub className="text-[9px] align-sub">free</sub></>;
  }

  return rowLabel;
}

export function canCompleteSection(section: AnalysisResultSection) {
  return Boolean(section.completion.completedBy.trim()) && Boolean(section.completion.completedDate);
}

const contentSnapshot = (section: AnalysisResultSection) => JSON.stringify({ ...section, completion: undefined, status: undefined, startedBy: undefined, startedDate: undefined });

/** Applies start/progress/completion rules after a section edit. startedBy is set only once. */
export function applySectionEditRules(previous: AnalysisResultSection, updated: AnalysisResultSection, email: string, today = new Date().toISOString().slice(0, 10)): AnalysisResultSection {
  let next = updated;
  if (contentSnapshot(previous) !== contentSnapshot(updated)) {
    next = {
      ...next,
      startedBy: previous.startedBy || email,
      startedDate: previous.startedDate || today,
      status: next.completion.isComplete ? next.status : AnalysisResultStatus.InProgress,
    };
  } else {
    next = { ...next, startedBy: previous.startedBy, startedDate: previous.startedDate };
  }

  if (next.completion.isComplete && !canCompleteSection(next)) {
    next = { ...next, completion: { ...next.completion, isComplete: false }, status: AnalysisResultStatus.InProgress };
  }
  return next;
}

export function hasSectionContent(section: AnalysisResultSection) {
  const hasValues = section.rows.some(row => [row.sampleValues, ...Object.values(row.sampleFields ?? {})].some(values => Object.values(values).some(value => value?.trim())));
  // Chip sections (mechanical preparation, SEM, XRD, ICP, LOM) keep their comment and files on each row.
  const hasRowNotes = section.rows.some(row => Boolean(row.comment?.trim()) || (row.files?.length ?? 0) > 0);
  return hasValues || hasRowNotes || Boolean(section.comment.trim()) || section.files.length > 0 || Boolean(section.completion.completedBy.trim()) || Boolean(section.completion.completedDate);
}

export function getSectionPresentation(section: AnalysisResultSection): SectionStatusPillProps {
  if (section.completion.isComplete || section.status === AnalysisResultStatus.Completed) {
    return { label: "Done", tone: "done" };
  }

  if (hasSectionContent(section)) {
    return { label: "In progress", tone: "progress" };
  }

  return { label: "Not started", tone: "neutral" };
}

export function formatShortDate(value?: string) {
  if (!value) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${year.slice(2)}/${month}/${day}`;
  }

  return value;
}

export function formatCompletionDate(value?: string) {
  if (!value) {
    return "";
  }

  const trimmedValue = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue.slice(2);
  }

  return trimmedValue;
}

export function parseCompletionDate(value?: string) {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim();
  if (/^\d{2}-\d{2}-\d{2}$/.test(trimmedValue)) {
    const [year, month, day] = trimmedValue.split("-");
    return new Date(`20${year}-${month}-${day}T00:00:00`);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return new Date(`${trimmedValue}T00:00:00`);
  }

  return null;
}

export function toCompletionDateValue(date: Date | null | undefined) {
  if (!date) {
    return "";
  }

  const year = date.getFullYear().toString().slice(2);
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const calendarStrings = {
  months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  shortMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  shortDays: ["S", "M", "T", "W", "T", "F", "S"],
  goToToday: "Go to today",
  prevMonthAriaLabel: "Go to previous month",
  nextMonthAriaLabel: "Go to next month",
  prevYearAriaLabel: "Go to previous year",
  nextYearAriaLabel: "Go to next year",
  prevYearRangeAriaLabel: "Go to previous year range",
  nextYearRangeAriaLabel: "Go to next year range",
  monthPickerHeaderAriaLabel: "{0}, select to change the year",
  yearPickerHeaderAriaLabel: "{0}, select to change the month",
  closeButtonAriaLabel: "Close calendar",
  selectedDateFormatString: "Selected date {0}",
  todayDateFormatString: "Today's date {0}",
  weekNumberFormatString: "Week number {0}",
  dayMarkedAriaLabel: "Marked day",
};

interface ResultCompletionDatePickerProps {
  value?: string;
  ariaLabel: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** A portal-based calendar avoids hidden native inputs and clipped picker popups. */
export function ResultCompletionDatePicker({ value, ariaLabel, onChange, disabled = false }: ResultCompletionDatePickerProps) {
  const [open, setOpen] = useState(false);
  const displayValue = formatCompletionDate(value).replace(/-/g, "/") || "YY/MM/DD";

  return (
    <Popover open={open && !disabled} onOpenChange={(_, data) => setOpen(data.open && !disabled)} positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className="flex h-[37px] w-[106px] shrink-0 items-center justify-center gap-1 border border-primary-dark/50 bg-primary-light px-1 text-[12px] text-oi-button-blue dark:border-primary-light/50 dark:bg-oi-dark-background dark:text-oi-powder-form-yellow focus:outline-none focus-visible:border-oi-button-blue dark:focus-visible:border-oi-powder-form-yellow"
        >
          <span>{displayValue}</span>
          <CalendarMonth20Regular aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverSurface className="!z-[10000] !p-0 border border-primary-dark/50 bg-primary-light text-primary-dark dark:border-primary-light/50 dark:bg-oi-dark-background dark:text-primary-light">
        <Calendar
          value={parseCompletionDate(value) ?? new Date()}
          onSelectDate={date => {
            onChange(toCompletionDateValue(date));
            setOpen(false);
          }}
          strings={calendarStrings}
          showGoToToday
        />
      </PopoverSurface>
    </Popover>
  );
}

export function displayResultValue(value?: string) {
  return value?.trim() ? value : "-";
}

export function SectionStatusPill({ label, tone }: SectionStatusPillProps) {
  const className = tone === "done"
    ? "border-good-green bg-good-green/10 text-good-green"
    : tone === "review"
      ? "border-oi-button-blue dark:border-oi-yellow bg-oi-yellow/10 text-oi-button-blue dark:text-oi-yellow"
      : tone === "progress"
        ? "border-oi-button-blue dark:border-oi-yellow bg-oi-yellow/5 text-oi-button-blue dark:text-oi-yellow"
        : "border-oi-button-blue bg-oi-button-blue/10 text-primary-dark dark:text-primary-light";

  return <span className={`inline-flex min-w-[58px] items-center justify-center rounded-[3px] border px-1.5 py-0.5 text-[9px] font-semibold ${className}`}>{label}</span>;
}
